import { useEffect, useState, useCallback } from 'react';

interface TouchpointData {
  touchpoint_number: number;
  gclid?: string | null;
  fbclid?: string | null;
  msclkid?: string | null;
  ttclid?: string | null;
  epik?: string | null;
  twclid?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
  utm_adgroup?: string | null;
  captured_at: string;
}

interface TrackingData {
  visitor_id: string;
  gclid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  ttclid: string | null;
  epik: string | null;
  twclid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  utm_adgroup: string | null;
  gclid_captured_at: string | null;
  touchpoints: TouchpointData[];
}

const TRACKING_STORAGE_KEY = 'fsta_visitor_tracking';
const VISITOR_ID_KEY = 'fsta_visitor_id';
const TRACKING_SENT_KEY = 'fsta_tracking_sent';
const CLICK_ID_PARAMS = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'epik', 'twclid'];
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_adgroup'];
const ALL_URL_PARAMS = [...CLICK_ID_PARAMS, ...UTM_PARAMS];

const getApiUrl = () => {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID || 'hkqzynkcqnfqrhcoejdx';
  return `https://${projectId}.supabase.co/functions/v1/track-visitor`;
};

// ── Visitor ID (persistent fingerprint) ──────────────────────────
// Generate once per device, persist in localStorage + cookie so it
// survives across sessions, tabs, and page refreshes.

const generateVisitorId = (): string => {
  return 'v_' + crypto.randomUUID();
};

const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
};

const getOrCreateVisitorId = (): string => {
  // Priority: localStorage > cookie > generate new
  let vid = localStorage.getItem(VISITOR_ID_KEY);
  if (vid) {
    setCookie(VISITOR_ID_KEY, vid, 730); // refresh cookie expiry (2 years)
    return vid;
  }
  vid = getCookie(VISITOR_ID_KEY);
  if (vid) {
    localStorage.setItem(VISITOR_ID_KEY, vid);
    return vid;
  }
  vid = generateVisitorId();
  localStorage.setItem(VISITOR_ID_KEY, vid);
  setCookie(VISITOR_ID_KEY, vid, 730);
  return vid;
};

// ── Public helpers ───────────────────────────────────────────────

export const getStoredTracking = (): TrackingData | null => {
  try {
    const stored = localStorage.getItem(TRACKING_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as TrackingData;
      // Ensure visitor_id is always present
      if (!parsed.visitor_id) {
        parsed.visitor_id = getOrCreateVisitorId();
        localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(parsed));
      }
      return parsed;
    }
  } catch { /* ignore */ }
  return null;
};

export const getVisitorId = (): string => getOrCreateVisitorId();

export const appendTrackingToUrl = (url: string): string => {
  const tracking = getStoredTracking();
  if (!tracking) return url;
  const urlObj = new URL(url, window.location.origin);
  ALL_URL_PARAMS.forEach(param => {
    const val = (tracking as unknown as Record<string, string | null>)[param];
    if (val) urlObj.searchParams.set(param, val);
  });
  return urlObj.toString();
};

// ── Deduplication helper ─────────────────────────────────────────
// Two touchpoints are considered duplicates if every click-id and
// UTM value is identical. Prevents re-recording on page refresh.

const isSameTouchpoint = (a: Record<string, string | null>, b: TouchpointData): boolean => {
  return ALL_URL_PARAMS.every(p => {
    const aVal = a[p] || null;
    const bVal = (b as unknown as Record<string, string | null>)[p] || null;
    return aVal === bVal;
  });
};

// ── Core: persist from URL ───────────────────────────────────────

const persistTrackingFromUrl = (): { stored: TrackingData; hasNewParams: boolean } => {
  const params = new URLSearchParams(window.location.search);
  const visitorId = getOrCreateVisitorId();
  const existingStored = getStoredTracking() || {
    visitor_id: visitorId,
    gclid: null, fbclid: null, msclkid: null, ttclid: null, epik: null, twclid: null,
    utm_source: null, utm_medium: null, utm_campaign: null, utm_term: null, utm_content: null, utm_adgroup: null,
    gclid_captured_at: null, touchpoints: [],
  } as TrackingData;

  // Always ensure visitor_id is set
  existingStored.visitor_id = visitorId;

  // Collect new params from URL
  const newParamValues: Record<string, string | null> = {};
  let hasAnyParam = false;
  ALL_URL_PARAMS.forEach(param => {
    const value = params.get(param);
    if (value) {
      newParamValues[param] = value;
      hasAnyParam = true;
    } else {
      newParamValues[param] = null;
    }
  });

  if (!hasAnyParam) {
    // No tracking params in URL – just ensure storage is up to date
    localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(existingStored));
    return { stored: existingStored, hasNewParams: false };
  }

  // ── Dedup check: don't record if identical to last touchpoint ──
  const existingTouchpoints = existingStored.touchpoints || [];
  const lastTP = existingTouchpoints[existingTouchpoints.length - 1];
  if (lastTP && isSameTouchpoint(newParamValues, lastTP)) {
    // Same params as last touchpoint – skip (e.g., page refresh)
    return { stored: existingStored, hasNewParams: false };
  }

  const now = new Date().toISOString();
  const nextNumber = existingTouchpoints.length + 1;

  const newTouchpoint: TouchpointData = {
    touchpoint_number: nextNumber,
    gclid: newParamValues.gclid || null,
    fbclid: newParamValues.fbclid || null,
    msclkid: newParamValues.msclkid || null,
    ttclid: newParamValues.ttclid || null,
    epik: newParamValues.epik || null,
    twclid: newParamValues.twclid || null,
    utm_source: newParamValues.utm_source || null,
    utm_medium: newParamValues.utm_medium || null,
    utm_campaign: newParamValues.utm_campaign || null,
    utm_term: newParamValues.utm_term || null,
    utm_content: newParamValues.utm_content || null,
    utm_adgroup: newParamValues.utm_adgroup || null,
    captured_at: now,
  };

  const stored: TrackingData = {
    visitor_id: visitorId,
    gclid: newParamValues.gclid || existingStored.gclid || null,
    fbclid: newParamValues.fbclid || existingStored.fbclid || null,
    msclkid: newParamValues.msclkid || existingStored.msclkid || null,
    ttclid: newParamValues.ttclid || existingStored.ttclid || null,
    epik: newParamValues.epik || existingStored.epik || null,
    twclid: newParamValues.twclid || existingStored.twclid || null,
    utm_source: newParamValues.utm_source || existingStored.utm_source || null,
    utm_medium: newParamValues.utm_medium || existingStored.utm_medium || null,
    utm_campaign: newParamValues.utm_campaign || existingStored.utm_campaign || null,
    utm_term: newParamValues.utm_term || existingStored.utm_term || null,
    utm_content: newParamValues.utm_content || existingStored.utm_content || null,
    utm_adgroup: newParamValues.utm_adgroup || existingStored.utm_adgroup || null,
    gclid_captured_at: newParamValues.gclid ? now : existingStored.gclid_captured_at || null,
    touchpoints: [...existingTouchpoints, newTouchpoint],
  };

  localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(stored));
  sessionStorage.setItem(TRACKING_SENT_KEY, 'false');
  return { stored, hasNewParams: true };
};

// ── Send to server ───────────────────────────────────────────────

const sendTrackingToServer = async (trackingData: TrackingData): Promise<void> => {
  try {
    const latestTouchpoint = trackingData.touchpoints?.[trackingData.touchpoints.length - 1];
    if (!latestTouchpoint) return;

    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitor_id: trackingData.visitor_id,
        ...latestTouchpoint,
      }),
    });

    if (response.ok) {
      sessionStorage.setItem(TRACKING_SENT_KEY, 'true');
    }
  } catch (error) {
    console.error('Failed to send tracking to server:', error);
  }
};

// ── Fetch from server ────────────────────────────────────────────

const fetchTrackingFromServer = async (visitorId: string): Promise<TrackingData | null> => {
  try {
    const response = await fetch(getApiUrl(), {
      method: 'GET',
      headers: { 'X-Visitor-Id': visitorId },
    });
    if (response.ok) {
      const data = await response.json();
      if (data.touchpoints && data.touchpoints.length > 0) {
        const latest = data.touchpoints[data.touchpoints.length - 1];
        return {
          visitor_id: visitorId,
          gclid: latest.gclid || null,
          fbclid: latest.fbclid || null,
          msclkid: latest.msclkid || null,
          ttclid: latest.ttclid || null,
          epik: latest.epik || null,
          twclid: latest.twclid || null,
          utm_source: latest.utm_source || null,
          utm_medium: latest.utm_medium || null,
          utm_campaign: latest.utm_campaign || null,
          utm_term: latest.utm_term || null,
          utm_content: latest.utm_content || null,
          utm_adgroup: latest.utm_adgroup || null,
          gclid_captured_at: latest.gclid ? latest.captured_at : null,
          touchpoints: data.touchpoints,
        };
      }
      return null;
    }
  } catch (error) {
    console.error('Failed to fetch tracking from server:', error);
  }
  return null;
};

// ── Run immediately on module load (before React mounts) ─────────
if (typeof window !== 'undefined') {
  const { stored, hasNewParams } = persistTrackingFromUrl();
  if (hasNewParams) {
    sendTrackingToServer(stored);
  } else {
    const sentStatus = sessionStorage.getItem(TRACKING_SENT_KEY);
    if (sentStatus !== 'true' && stored.touchpoints?.length > 0) {
      sendTrackingToServer(stored);
    }
  }
}

// ── React hook ───────────────────────────────────────────────────

export const useVisitorTracking = () => {
  const [tracking, setTracking] = useState<TrackingData | null>(() => getStoredTracking());
  const [isLoading, setIsLoading] = useState(true);

  const refreshTracking = useCallback(async () => {
    const visitorId = getOrCreateVisitorId();
    const localTracking = getStoredTracking();
    const serverTracking = await fetchTrackingFromServer(visitorId);

    if (serverTracking && localTracking) {
      // Merge: combine server & local touchpoints, deduplicate by content fingerprint
      const fingerprint = (t: TouchpointData) => [t.gclid, t.fbclid, t.msclkid, t.ttclid, t.epik, t.twclid, t.utm_source, t.utm_medium, t.utm_campaign, t.utm_term, t.utm_content, t.utm_adgroup].map(v => v || '').join('|');
      const allTouchpoints = [...(serverTracking.touchpoints || [])];
      const serverFingerprints = new Set(allTouchpoints.map(fingerprint));
      (localTracking.touchpoints || []).forEach(t => {
        if (!serverFingerprints.has(fingerprint(t))) {
          allTouchpoints.push(t);
        }
      });
      // Re-number sequentially after merge
      allTouchpoints.sort((a, b) => new Date(a.captured_at).getTime() - new Date(b.captured_at).getTime());
      allTouchpoints.forEach((t, i) => { t.touchpoint_number = i + 1; });

      const merged: TrackingData = { ...localTracking, touchpoints: allTouchpoints, visitor_id: visitorId };
      setTracking(merged);
      localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(merged));
    } else if (serverTracking) {
      setTracking(serverTracking);
      localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(serverTracking));
    } else if (localTracking) {
      setTracking(localTracking);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    refreshTracking();
  }, [refreshTracking]);

  return { tracking, isLoading, refreshTracking };
};
